/* Anki-style flashcard system — generation, deck library, study mode.
 *
 * Used as a tool inside SubjectDetail. Teacher hits "Generate cards" to build
 * a deck from the chapter pages; previously-built decks live in a grid.
 * "Study" opens a fullscreen flipper.
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import {
  X, Loader2, Wand2, Layers, Trash2, ChevronLeft, ChevronRight,
  Check, Sparkles, RotateCcw, GraduationCap,
} from "lucide-react";

const TAG_COLORS = {
  people: "#7C3AED", place: "#0EA5E9", event: "#F59E0B",
  concept: "#10B981", date: "#EF4444", other: "#A855F7",
};

/* ───────────────────────── main tool body ───────────────────────── */
export default function SmartCardsBody({ chapter, subject }) {
  const [decks, setDecks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [studyDeck, setStudyDeck] = useState(null);

  const reload = async () => {
    try {
      const { data } = await api.get("/ai/cards", { params: { chapter_id: chapter.id } });
      setDecks(data.decks || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [chapter.id]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/cards/generate", {
        subject: subject.name, topic: chapter.title, grade: subject.grade_name || "",
        subject_id: subject.id, chapter_id: chapter.id,
        pages: (chapter.pages || []).map((p) => ({ order: p.order, ocr_text: p.ocr_text, summary: p.summary })),
        n: 10,
      });
      toast.success(`${(data.deck.cards || []).length} cards ready 🎴`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Card generation failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this deck?")) return;
    try { await api.delete(`/ai/cards/${id}`); reload(); toast.success("Deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const hasPages = (chapter.pages || []).length > 0;
  return (
    <div data-testid="cards-body">
      <div className="flex items-end justify-between mb-1">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] font-bold" style={{ color: "#EC4899" }}>Smart Cards</div>
          <h2 className="font-display font-bold text-2xl tracking-tighter mt-1">Anki-style study deck 🎴</h2>
          <p className="text-sm text-neutral-500 mt-1">Front/back flashcards generated from every page · flip · keep / review.</p>
        </div>
      </div>
      <button
        onClick={generate}
        disabled={!hasPages || busy}
        data-testid="cards-generate"
        className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#EC4899] text-white text-sm font-medium hover:bg-[#DB2777] transition-colors disabled:opacity-50"
      >
        {busy ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Wand2 size={14} /> Generate 10 cards</>}
      </button>
      {!hasPages && <div className="text-[11px] text-neutral-500 mt-2">Scan a page first — cards are extracted from your OCR text.</div>}

      {decks.length > 0 && (
        <div className="mt-7">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-3">
            Saved decks · {decks.length}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {decks.map((d) => (
              <DeckCard key={d.id} deck={d} onStudy={() => setStudyDeck(d)} onDelete={() => remove(d.id)} />
            ))}
          </div>
        </div>
      )}

      <StudyOverlay deck={studyDeck} onClose={() => setStudyDeck(null)} />
    </div>
  );
}

function DeckCard({ deck, onStudy, onDelete }) {
  const cards = deck.cards || [];
  const tags = [...new Set(cards.map((c) => c.tag).filter(Boolean))].slice(0, 4);
  return (
    <div className="bg-gradient-to-br from-[#FCE7F3] to-[#FBCFE8] border border-[#EC4899]/20 rounded-md p-3 group">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#DB2777] font-bold">{cards.length} cards</div>
          <div className="font-display font-bold text-sm tracking-tighter text-[#831843] mt-1 line-clamp-2 leading-snug">{deck.title}</div>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {tags.map((t) => (
                <span key={t} className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm text-white"
                  style={{ background: TAG_COLORS[t] || "#A855F7" }}>{t}</span>
              ))}
            </div>
          )}
        </div>
        <button onClick={onDelete} data-testid={`deck-del-${deck.id}`} className="text-[#831843]/40 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={12} /></button>
      </div>
      <button onClick={onStudy} data-testid={`deck-study-${deck.id}`} className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-[#0A0C10] text-white text-xs font-medium hover:bg-[#0A0C10]/90 transition-colors">
        <GraduationCap size={11} /> Study
      </button>
    </div>
  );
}

/* ───────────────────────── study overlay ───────────────────────── */
export function StudyOverlay({ deck, onClose }) {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [gotIt, setGotIt] = useState({});
  useEffect(() => { if (deck) { setIdx(0); setFlipped(false); setGotIt({}); } }, [deck]);

  if (!deck) return null;
  const cards = deck.cards || [];
  const card = cards[idx];
  const total = cards.length;
  const masteredCount = Object.values(gotIt).filter(Boolean).length;

  const next = () => { setFlipped(false); idx < total - 1 ? setIdx(idx + 1) : finish(); };
  const prev = () => { setFlipped(false); setIdx(Math.max(0, idx - 1)); };
  const mark = (correct) => {
    setGotIt((m) => ({ ...m, [idx]: correct }));
    if (correct) {
      confetti({ particleCount: 30, spread: 60, origin: { y: 0.5 }, colors: ["#EC4899", "#FFD600", "#A855F7"] });
    }
    setTimeout(next, 300);
  };
  const finish = () => {
    confetti({ particleCount: 100, spread: 110, origin: { y: 0.5 }, colors: ["#EC4899", "#FFD600", "#A855F7", "#10B981"] });
    toast.success(`Mastered ${masteredCount}/${total} cards 🎉`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-[#0A0C10]/95 backdrop-blur-md flex flex-col" data-testid="cards-study-overlay">
      <div className="px-6 lg:px-10 pt-5 pb-3 flex items-start justify-between border-b border-white/10">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#EC4899] font-bold">/ study session</div>
          <h2 className="font-display font-bold text-2xl tracking-tighter text-white mt-1">{deck.title}</h2>
          <div className="text-xs text-white/50 mt-1 font-mono uppercase tracking-wider">
            Card {idx + 1} / {total} · Mastered {masteredCount}
          </div>
        </div>
        <button onClick={onClose} data-testid="study-close" className="w-10 h-10 rounded-md bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center"><X size={18} /></button>
      </div>

      <div className="h-1 bg-white/10">
        <motion.div className="h-full bg-gradient-to-r from-[#EC4899] via-[#A855F7] to-[#FFD600]"
          initial={{ width: 0 }} animate={{ width: `${((idx + 1) / total) * 100}%` }} transition={{ duration: 0.4 }} />
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-3xl"
          >
            <button
              onClick={() => setFlipped((f) => !f)}
              data-testid="study-flip"
              className="relative w-full aspect-[16/10] perspective-[1200px]"
              style={{ perspective: "1200px" }}
            >
              <motion.div
                animate={{ rotateY: flipped ? 180 : 0 }}
                transition={{ duration: 0.6 }}
                className="absolute inset-0"
                style={{ transformStyle: "preserve-3d" }}
              >
                {/* Front */}
                <div
                  className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#FCE7F3] via-white to-[#FBCFE8] border-2 border-[#EC4899]/20 shadow-2xl flex flex-col items-center justify-center p-10 text-center"
                  style={{ backfaceVisibility: "hidden" }}
                >
                  <span
                    className="px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wider text-white mb-4"
                    style={{ background: TAG_COLORS[card?.tag] || "#A855F7" }}
                  >{card?.tag || "card"}</span>
                  <div className="font-display font-bold text-3xl sm:text-4xl text-[#831843] tracking-tighter leading-tight">{card?.front}</div>
                  <div className="mt-8 text-[11px] font-mono uppercase tracking-[0.22em] text-[#DB2777]/70">Tap to flip</div>
                </div>
                {/* Back */}
                <div
                  className="absolute inset-0 rounded-2xl bg-[#0A0C10] border-2 border-[#EC4899]/40 shadow-2xl flex flex-col items-center justify-center p-10 text-center"
                  style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
                >
                  <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#EC4899] mb-3">Answer</div>
                  <div className="font-display font-bold text-2xl sm:text-3xl text-white tracking-tight leading-snug">{card?.back}</div>
                  {card?.example && (
                    <div className="mt-6 text-sm text-white/60 italic max-w-xl">"{card.example}"</div>
                  )}
                </div>
              </motion.div>
            </button>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="px-6 lg:px-10 py-5 border-t border-white/10 flex items-center justify-between">
        <button onClick={prev} disabled={idx === 0} data-testid="study-prev"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm disabled:opacity-30 transition-colors">
          <ChevronLeft size={14} /> Previous
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => mark(false)} data-testid="study-review"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors">
            <RotateCcw size={14} /> Review again
          </button>
          <button onClick={() => mark(true)} data-testid="study-got"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#10B981] hover:bg-[#059669] text-white text-sm font-semibold transition-colors">
            <Check size={14} /> Got it!
          </button>
        </div>
        <button onClick={next} data-testid="study-next"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white text-[#0A0C10] text-sm font-semibold transition-colors">
          {idx < total - 1 ? <>Next <ChevronRight size={14} /></> : <>Finish <Sparkles size={14} /></>}
        </button>
      </div>
    </div>
  );
}
